import {
  faCity,
  faHouseChimney,
  faKey,
  faLocationDot,
  faMapLocationDot,
  faMoneyBill1Wave,
  faSeedling,
  faSparkles,
} from '@fortawesome/pro-duotone-svg-icons'

import { ImportedListing } from '../../_types/imported-listing'
import { QuickPassProfile } from '../../_types/quickpass-profile'
import { RenterPersonality, RenterStatsSummary } from './types'

/**
 * Computes the renter-facing stats summary from their imported listings.
 * Returns sensible empties when there's not enough data to show.
 *
 * @param {ImportedListing[]} listings - the renter's tracked listings
 * @returns {RenterStatsSummary} the summary stats for the dashboard strip
 */
const buildRenterStatsSummary = (
  listings: ImportedListing[],
): RenterStatsSummary => {
  if (listings.length === 0) {
    return {
      trackingCount: 0,
      averagePrice: null,
      topSavings: 0,
      hasMeaningfulData: false,
    }
  }

  const validPrices = listings
    .map(listing => listing.priceMonthly)
    .filter((price): price is number => typeof price === 'number')

  const averagePrice =
    validPrices.length === 0
      ? null
      : Math.round(
          validPrices.reduce((sum, price) => sum + price, 0) /
            validPrices.length,
        )

  let topSavings = 0
  for (const listing of listings) {
    const validHistory = listing.priceHistory.filter(
      entry => typeof entry.priceMonthly === 'number',
    )

    if (validHistory.length < 2) {
      continue
    }

    const firstPrice = validHistory[0].priceMonthly as number
    const lastPrice = validHistory[validHistory.length - 1]
      .priceMonthly as number

    if (firstPrice > lastPrice) {
      topSavings = Math.max(topSavings, firstPrice - lastPrice)
    }
  }

  return {
    trackingCount: listings.length,
    averagePrice,
    topSavings,
    hasMeaningfulData: true,
  }
}

/**
 * Picks a fun renter-personality label based on their unit preferences.
 * Pure flavour — no functional impact — but it makes the dashboard feel
 * personal. Each branch checks for a distinctive preference shape and
 * returns a label, emoji, and one-liner hint.
 *
 * @param {QuickPassProfile | null} profile - the renter profile
 * @returns {RenterPersonality | null} the personality label, or null when no profile
 */
const buildRenterPersonality = (
  profile: QuickPassProfile | null,
): RenterPersonality | null => {
  if (profile === null) {
    return null
  }

  const preferences = profile.unitPreferences
  const cities = preferences.cities
  const beds = preferences.bedroomCounts
  const types = preferences.propertyTypes
  const priceMax = preferences.priceMax ?? Infinity

  if (beds.includes(0) === true) {
    return {
      icon: faSeedling,
      label: 'The Studio Minimalist',
      hint: 'Less square footage, more soul.',
    }
  }

  if (types.includes('house') === true && beds.some(count => count >= 3)) {
    return {
      icon: faHouseChimney,
      label: 'The House Hunter',
      hint: 'Big space, big plans.',
    }
  }

  if (types.includes('condo') === true && priceMax >= 3000) {
    return {
      icon: faCity,
      label: 'The Condo Connoisseur',
      hint: 'High floors, higher standards.',
    }
  }

  if (cities.length >= 3) {
    return {
      icon: faMapLocationDot,
      label: 'The Multi-City Wanderer',
      hint: 'Keeping options open across the map.',
    }
  }

  if (cities.length === 1) {
    const cityName = cities[0]
    return {
      icon: faLocationDot,
      label: `The ${cityName} Devotee`,
      hint: `It's ${cityName} or nothing.`,
    }
  }

  if (priceMax <= 2000) {
    return {
      icon: faMoneyBill1Wave,
      label: 'The Budget Sniper',
      hint: 'Every dollar gets scrutinised.',
    }
  }

  if (preferences.dwellingScope === 'whole-property') {
    return {
      icon: faKey,
      label: 'The Whole-Place Aficionado',
      hint: 'Sharing walls is for the weak.',
    }
  }

  return {
    icon: faSparkles,
    label: 'The Open-Minded Renter',
    hint: 'Keeping it flexible.',
  }
}

/**
 * Formats a price value into a $X,XXX/mo string with thousands separator.
 *
 * @param {number | null} price - the price to format, or null when unknown
 * @returns {string} the formatted price or "—"
 */
const formatStatPrice = (price: number | null): string => {
  if (price === null) {
    return '—'
  }

  return `$${price.toLocaleString('en-CA')}`
}

export { buildRenterStatsSummary, buildRenterPersonality, formatStatPrice }
